import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";
import { API } from "../App";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";

// Founder-only: reveal who is behind an anonymous label. Requires a reason; every reveal is audit-logged.
export const RevealAuthorDialog = ({ open, onOpenChange, contentType, contentId, testIdPrefix }) => {
  const [reason, setReason] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const close = (o) => { onOpenChange(o); if (!o) { setReason(""); setResult(null); } };

  const reveal = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/founder/anonymous/reveal`, { content_type: contentType, content_id: contentId, reason });
      setResult(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? "Please give a reason of at least 10 characters" : (d || "Couldn't reveal author"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md" data-testid={`${testIdPrefix}-reveal-dialog`} onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            <ShieldAlert className="w-4 h-4 text-[#B45309]" /> Reveal anonymous author
          </DialogTitle>
          <DialogDescription>Only for moderation. Your reason and this reveal are permanently recorded in the audit log.</DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="rounded-xl bg-[#FAF7F2] border border-[#E5DFEC] p-4 text-sm" data-testid={`${testIdPrefix}-reveal-result`}>
            {result.found ? (
              <><p className="font-medium text-[#6B4E8A]">{result.name}</p><p className="text-[#475569]">{result.email}</p><p className="text-xs text-[#94A3B8] mt-1">Behind “{result.label}”</p></>
            ) : <p className="text-[#64748B]">The author's account no longer exists.</p>}
          </div>
        ) : (
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500} placeholder="Why do you need to see the author? (min 10 characters)" className="bg-white border-[#E5DFEC]" data-testid={`${testIdPrefix}-reveal-reason`} />
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => close(false)}>{result ? "Close" : "Cancel"}</Button>
          {!result && (
            <Button onClick={reveal} disabled={busy || reason.trim().length < 10} className="rounded-full bg-[#B45309] hover:bg-[#92400E] text-white" data-testid={`${testIdPrefix}-reveal-submit`}>
              {busy ? "Revealing..." : "Reveal & log"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RevealAuthorDialog;
